import { SurveyType } from '@commutifi/models/Surveys'
import { FormattedMessage } from 'locales/index'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'
import { Onboarding, OnboardingProps, withSurveyContext } from 'shared/components/common/Onboarding'
import { GenericError } from '@commutifi-fe/ui'
import { defaultProcessSurveyError, defaultProcessSurveySuccess, useProcessSurvey } from 'shared/hooks/useProcessSurvey'
import { EnterpriseSurveyPages, EnterpriseSurveyPathsParams, PublicRouting } from 'src/routing'
import { authActions, authSelectors } from 'store/modules/auth'
import { selectors } from 'store/modules/enterpriseSurvey'
import { localSettingsSelectors } from 'store/modules/localSettings'
import { wrapActionInPromise } from 'utils/helpers'
import NotFound from 'views/Anonymous/views/404'
import RegisterScreen from 'views/Anonymous/views/Onboarding/RegisterScreen'
import { RouteProps, SurveyFormContainer } from 'views/Anonymous/views/Onboarding/SurveyForm'
import SurveySuccessScreen from 'views/Anonymous/views/Onboarding/SurveySuccessScreen'
import { SurveyLandingPageContainer } from './SurveyLandingPage'

export const UnauthenticatedOnboarding = withSurveyContext<RouteComponentProps>((props: OnboardingProps) => {
  /**
   * Hooks
   */
  const locale = useSelector(localSettingsSelectors.getLocale)
  const surveyId = useSelector(selectors.getSurveyId)
  const isAnonymous = useSelector(selectors.getIsAnonymous)
  const isDirectOnboarding = useSelector(selectors.getIsDirectOnboarding)
  const isProfileUpdateRequired = useSelector(selectors.makeGetPrefilledData('isProfileUpdateRequired'))
  const surveyType = useSelector(selectors.makeGetSurvey('type'))
  const isAuthenticated = useSelector(authSelectors.isLoggedIn)
  const urlShortlink = props.match.params.shortlink

  const dispatch = useDispatch()
  const logout = React.useCallback(
    (...args: Parameters<typeof authActions.logout>) => dispatch(authActions.logout(...args)),
    [dispatch]
  )

  const hideAccountAlreadyExist = React.useMemo(
    () => props.surveyType === SurveyType.followUp || isDirectOnboarding === false || isAnonymous,
    [isAnonymous, isDirectOnboarding, props.surveyType]
  )

  const [logoutError, setLogoutError] = React.useState(null)
  React.useEffect(() => {
    // Check if user is login and log him out if it is the case
    const asyncLogout = async () => {
      try {
        // If the user is logged in on another account, we need to
        // logout this account before starting the onboarding flow
        // in case there is direct onboarding
        if (
          urlShortlink &&
          urlShortlink !== '_' &&
          isAuthenticated &&
          surveyType === SurveyType.onboarding &&
          !isProfileUpdateRequired
        ) {
          await wrapActionInPromise(logout)
        }
      } catch (e) {
        setLogoutError(e)
      }
    }
    asyncLogout()
  }, [urlShortlink, surveyType, isAuthenticated, isProfileUpdateRequired, logout])

  if (logoutError) {
    return (
      <GenericError
        type="page"
        error={logoutError}
        title={<FormattedMessage id="error.logout.pleaseLogout" />}
        description={<FormattedMessage id="error.logout.pleaseLogout.description" />}
      />
    )
  }

  const { processSurvey, loading: isProcessingSurvey } = useProcessSurvey(surveyId, {
    onSuccess: defaultProcessSurveySuccess(props.history, props.location.pathname),
    onError: defaultProcessSurveyError(props.history, props.location.pathname)
  })

  /**
   * Handle functions
   */
  const handleOnStartSurveyClick: React.MouseEventHandler = React.useCallback(
    (e) => {
      e.preventDefault()

      if (
        props.surveyType === SurveyType.followUp ||
        (props.surveyType === SurveyType.onboarding && isProfileUpdateRequired)
      ) {
        props.history.push(EnterpriseSurveyPages.Auth)
      } else {
        props.history.push(`${EnterpriseSurveyPages.Onboarding}/${EnterpriseSurveyPathsParams.Step}1`)
      }
    },
    [isProfileUpdateRequired, props.history, props.surveyType]
  )

  const handleSubmitSurvey = React.useCallback(() => {
    if (isAnonymous) {
      processSurvey({ preferredLocale: locale })
    } else {
      props.history.push(EnterpriseSurveyPages.Register)
    }
  }, [isAnonymous, locale, processSurvey, props.history])

  const handleGoToLogin = React.useCallback(() => props.history.push(PublicRouting.auth.getRoute()), [props.history])

  const LandingPage = React.useCallback(
    () => (
      <SurveyLandingPageContainer
        onStartClick={handleOnStartSurveyClick}
        redirectToLogin={hideAccountAlreadyExist ? undefined : handleGoToLogin}
      />
    ),
    [handleGoToLogin, handleOnStartSurveyClick, hideAccountAlreadyExist]
  )

  return (
    <Onboarding {...props} history={props.history}>
      <Switch>
        <Route exact path={PublicRouting.enterpriseSurvey.onboarding.register.path} component={RegisterScreen} />
        <Route exact path={PublicRouting.enterpriseSurvey.onboarding.success.path} component={SurveySuccessScreen} />
        <Route
          exact
          path={PublicRouting.enterpriseSurvey.onboarding.step.path}
          render={(props: RouteProps) => (
            <SurveyFormContainer {...props} isLoading={isProcessingSurvey} onSurveyFinish={handleSubmitSurvey} />
          )}
        />
        <Route
          exact
          path={PublicRouting.enterpriseSurvey.onboarding.path}
          render={() => (
            <Redirect to={`${PublicRouting.enterpriseSurvey.onboarding.path}/${EnterpriseSurveyPathsParams.Step}1`} />
          )}
        />
        <Route exact path={props.match.url} component={LandingPage} />
        <Route path="*" component={NotFound} />
      </Switch>
    </Onboarding>
  )
})

export default UnauthenticatedOnboarding
